import React from 'react';
import { useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { formatDate } from '../../Helpers/dateHelpers';
import {
    faCrown,
    faClock,
    faCalendarAlt,
    faCheckCircle,
    faTimesCircle,
    faMoneyBillWave,
    faGem,
    faStar,
    faShieldAlt,
    faRocket,
    faHourglassHalf,
    faSync,
    faPuzzlePiece,
    faPlusCircle,
    faUsers
} from '@fortawesome/free-solid-svg-icons';
import { motion } from 'framer-motion';
import { useLanguage } from '../../contexts/LanguageContext';

const SubscriptionViewer = ({ subscriptionData, userAddons }) => {
    const { t } = useLanguage();
    const navigate = useNavigate();
    if (!subscriptionData) {
        return (
            <div className="subscription-viewer-empty">
                <FontAwesomeIcon icon={faGem} className="empty-icon" />
                <p>{t('noSubscriptionData')}</p>
            </div>
        );
    }

    const {
        user_name,
        plan_name,
        subscription_date,
        subscription_date_expiration,
        plan_feature = [],
        notes,
        subscription_state,
        subscription_type,
        days_remaining,
        subscription_days,
        plan_period,
        plan_price,
        delayed_days,
        delayed_days_last_date,
        user_limit,
        user_remaining,
        storage_limit,
        storage_remaining
    } = subscriptionData;

    const activeAddons = userAddons || subscriptionData.user_addons || [];

    // Determine subscription status color and icon
    const getStatusConfig = () => {
        if (subscription_state === 'Promoted') {
            return {
                color: '#10B981',
                bgColor: '#ECFDF5',
                icon: faCheckCircle,
                label: t('active')
            };
        } else if (subscription_state === 'Expired') {
            return {
                color: '#EF4444',
                bgColor: '#FEF2F2',
                icon: faTimesCircle,
                label: t('statusExpired')
            };
        } else {
            return {
                color: '#6366f1',
                bgColor: '#FEF3C7',
                icon: faClock,
                label: t('statusPending')
            };
        }
    };

    const statusConfig = getStatusConfig();

    // Determine plan type styling
    const getPlanTypeConfig = () => {
        if (subscription_type === 'Premium') {
            return {
                color: '#8B5CF6',
                gradient: 'linear-gradient(135deg, #8B5CF6 0%, #6366F1 100%)',
                icon: faCrown
            };
        } else if (subscription_type === 'Pro') {
            return {
                color: '#EF4444',
                gradient: 'linear-gradient(135deg, #6366f1 0%, #EF4444 100%)',
                icon: faRocket
            };
        } else {
            return {
                color: '#3B82F6',
                gradient: 'linear-gradient(135deg, #3B82F6 0%, #06B6D4 100%)',
                icon: faShieldAlt
            };
        }
    };

    const planConfig = getPlanTypeConfig();

    const containerVariants = {
        hidden: { opacity: 0, y: 20 },
        visible: {
            opacity: 1,
            y: 0,
            transition: {
                duration: 0.4,
                staggerChildren: 0.1
            }
        }
    };

    const itemVariants = {
        hidden: { opacity: 0, x: -20 },
        visible: { opacity: 1, x: 0 }
    };

    const renderHighlightedNumbers = (value) => {
        if (value == null) {
            return 'غير محدد';
        }

        const textValue = String(value);
        const parts = textValue.split(/([0-9٠-٩]+(?:[.,][0-9٠-٩]+)?)/g);

        return parts.map((part, index) => (
            /[0-9٠-٩]/.test(part)
                ? <span key={`num-${index}`} className="detail-number-highlight">{part}</span>
                : part
        ));
    };

    return (
        <motion.div
            className="subscription-viewer-container"
            variants={containerVariants}
            initial="hidden"
            animate="visible"
        >
            {/* Hero Card */}
            <motion.div
                className="subscription-hero-card"
                variants={itemVariants}
            >
                <div className="hero-icon-bg" style={{ color: planConfig.color }}>
                    <FontAwesomeIcon icon={planConfig.icon} />
                </div>
                <div className="hero-content">
                    <div className="hero-header">
                        <div className="hero-title-section">
                            <div className="hero-icon-wrapper" style={{ background: planConfig.gradient }}>
                                <FontAwesomeIcon icon={planConfig.icon} className="hero-icon" />
                            </div>
                            <div>
                                <h1>{plan_name}</h1>
                                <p className="hero-subtitle">{user_name}</p>
                            </div>
                        </div>
                        <div
                            className="status-badge"
                            style={{
                                background: statusConfig.bgColor,
                                color: statusConfig.color
                            }}
                        >
                            <FontAwesomeIcon icon={statusConfig.icon} />
                            <span>{statusConfig.label}</span>
                        </div>
                    </div>

                    <div className="hero-stats-grid">
                        <div className="hero-stat">
                            <FontAwesomeIcon icon={faClock} />
                            <div>
                                <span className="stat-label">{t('remainingDaysLabel')}</span>
                                <span className="stat-value">{t('nDays').replace('{count}', days_remaining)}</span>
                            </div>
                        </div>
                        <div className="hero-stat">
                            <FontAwesomeIcon icon={faMoneyBillWave} />
                            <div>
                                <span className="stat-label">{t('planPrice')}</span>
                                <span className="stat-value">{plan_price} {t('currencyEGP')}</span>
                            </div>
                        </div>
                        <div className="hero-stat">
                            <FontAwesomeIcon icon={faCalendarAlt} />
                            <div>
                                <span className="stat-label">{t('subscriptionPeriod')}</span>
                                <span className="stat-value">{plan_period}</span>
                            </div>
                        </div>
                    </div>
                </div>
            </motion.div>

            {/* Renew Subscription Button */}
            <motion.div
                className="renew-subscription-section"
                variants={itemVariants}
            >
                <motion.button
                    className="renew-subscription-btn"
                    onClick={() => navigate('/pricing')}
                    whileHover={{ scale: 1.02, y: -2 }}
                    whileTap={{ scale: 0.98 }}
                >
                    <FontAwesomeIcon icon={faSync} className="renew-icon" />
                    <span>{t('renewSubscription')}</span>
                </motion.button>
            </motion.div>

            {/* Subscription Details Grid */}
            <div className="subscription-details-grid">
                {/* Dates Card */}
                <motion.div className="detail-card" variants={itemVariants}>
                    <div className="detail-card-header">
                        <FontAwesomeIcon icon={faCalendarAlt} />
                        <h3>{t('subscriptionDetails')}</h3>
                    </div>
                    <div className="detail-card-content">
                        <div className="detail-row">
                            <span className="detail-label">{t('startDate')}</span>
                            <span className="detail-value">{formatDate(subscription_date)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">{t('expirationDate')}</span>
                            <span className="detail-value">{formatDate(subscription_date_expiration)}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">{t('remainingDaysLabel')}</span>
                            <span className="detail-value">{renderHighlightedNumbers(t('nDays').replace('{count}', days_remaining))}</span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">{t('totalDays')}</span>
                            <span className="detail-value">
                                {renderHighlightedNumbers(subscription_days > 0 ? t('nDays').replace('{count}', subscription_days) : t('notSpecified'))}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">{t('studentLimit')}</span>
                            <span className="detail-value">
                                {renderHighlightedNumbers(t('studentLimitValue').replace('{remaining}', user_remaining).replace('{limit}', user_limit))}
                            </span>
                        </div>
                        <div className="detail-row">
                            <span className="detail-label">{t('storageLimit')}</span>
                            <span className="detail-value">
                                {renderHighlightedNumbers(
                                    storage_limit != null && storage_remaining != null
                                        ? t('storageLimitValue').replace('{remaining}', storage_remaining).replace('{limit}', storage_limit)
                                        : t('notSpecified')
                                )}
                            </span>
                        </div>

                    </div>
                </motion.div>

                {/* Notes Card */}
                {notes && (
                    <motion.div className="detail-card notes-card" variants={itemVariants}>
                        <div className="detail-card-header">
                            <FontAwesomeIcon icon={faStar} />
                            <h3>{t('notes')}</h3>
                        </div>
                        <div className="detail-card-content">
                            <p className="notes-text">{notes}</p>
                        </div>
                    </motion.div>
                )}

                {/* Delayed Days Card */}
                {delayed_days != null && delayed_days > 0 && (
                    <motion.div className="detail-card delayed-card" variants={itemVariants}>
                        <div className="detail-card-header">
                            <FontAwesomeIcon icon={faHourglassHalf} />
                            <h3>{t('delayedDaysTitle')}</h3>
                        </div>
                        <div className="detail-card-content">
                            <div className="detail-row">
                                <span className="detail-label">{t('delayedDaysCount')}</span>
                                <span className="detail-value delayed-value">{t('nDays').replace('{count}', delayed_days)}</span>
                            </div>
                            {delayed_days_last_date && (
                                <div className="detail-row">
                                    <span className="detail-label">{t('lastDelayDateLabel')}</span>
                                    <span className="detail-value">{formatDate(delayed_days_last_date)}</span>
                                </div>
                            )}
                        </div>
                    </motion.div>
                )}
            </div>

            {/* Add-ons Section */}
            {activeAddons && activeAddons.length > 0 && (
                <motion.div className="addons-section" variants={itemVariants}>
                    <div className="addons-header">
                        <FontAwesomeIcon icon={faPuzzlePiece} />
                        <h2>{t('activeAddons')}</h2>
                        <span className="addons-count">{t('addonsCount').replace('{count}', activeAddons.length)}</span>
                    </div>

                    <div className="addons-grid">
                        {activeAddons.map((addon, index) => (
                            <motion.div
                                key={index}
                                className="addon-card"
                                whileHover={{ scale: 1.03, y: -5 }}
                                transition={{ type: 'spring', stiffness: 300 }}
                            >
                                <div className="addon-icon-bg">
                                    <FontAwesomeIcon icon={addon.type.includes('Users') ? faUsers : faPlusCircle} />
                                </div>
                                <div className="addon-content">
                                    <div className="addon-header-info">
                                        <div className="addon-type-badge">{t('featuredAddon')}</div>
                                        <h4>{addon.type}</h4>
                                    </div>
                                    <div className="addon-value-section">
                                        <span className="addon-label">{t('addonExtraValue')}</span>
                                        <div className="addon-value-badge">
                                            <FontAwesomeIcon icon={faPlusCircle} />
                                            <span>{addon.extra_value}</span>
                                        </div>
                                    </div>
                                </div>
                                <motion.div
                                    className="addon-glow"
                                    animate={{
                                        opacity: [0.3, 0.6, 0.3],
                                        scale: [1, 1.1, 1]
                                    }}
                                    transition={{
                                        duration: 3,
                                        repeat: Infinity,
                                        ease: "easeInOut"
                                    }}
                                />
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            )}

            {/* Features Section */}
            <motion.div className="features-section" variants={itemVariants}>
                <div className="features-header">
                    <FontAwesomeIcon icon={faGem} />
                    <h2>{t('availableFeatures')}</h2>
                    <span className="features-count">{t('featuresCount').replace('{count}', plan_feature.filter(f => f.included).length)}</span>
                </div>

                <div className="features-grid">
                    {plan_feature.map((feature) => (
                        <motion.div
                            key={feature.id}
                            className={`feature-card ${feature.included ? 'included' : 'excluded'}`}
                            whileHover={{ scale: 1.02, y: -5 }}
                            transition={{ type: 'spring', stiffness: 300 }}
                        >
                            <div className="feature-icon-wrapper">
                                <FontAwesomeIcon
                                    icon={feature.included ? faCheckCircle : faTimesCircle}
                                    className="feature-icon"
                                />
                            </div>
                            <div className="feature-content">
                                <h4>{feature.name}</h4>
                                <span className="feature-screen">{feature.screen_name}</span>
                            </div>
                            {feature.included && (
                                <div className="feature-badge">
                                    <FontAwesomeIcon icon={faStar} />
                                </div>
                            )}
                        </motion.div>
                    ))}
                </div>
            </motion.div>

            <style jsx>{`
                .subscription-viewer-container {
                    padding: 24px;
                    max-width: 1400px;
                    margin: 0 auto;
                      font-family: "Zain", sans-serif;
                }

                .subscription-viewer-empty {
                    text-align: center;
                    padding: 80px 20px;
                    color: #64748B;
                }

                .empty-icon {
                    font-size: 4rem;
                    color: #CBD5E1;
                    margin-bottom: 20px;
                }

                /* Hero Card */
                .subscription-hero-card {
                    background: white;
                    border-radius: 32px;
                    padding: 40px;
                    margin-bottom: 30px;
                    position: relative;
                    overflow: hidden;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
                    border: 1px solid rgba(255, 255, 255, 1);
                }

                .hero-icon-bg {
                    position: absolute;
                    right: -50px;
                    top: -50px;
                    font-size: 15rem;
                    opacity: 0.03;
                    transform: rotate(-15deg);
                }

                .hero-content {
                    position: relative;
                    z-index: 1;
                }

                .hero-header {
                    display: flex;
                    justify-content: space-between;
                    align-items: flex-start;
                    margin-bottom: 30px;
                    flex-wrap: wrap;
                    gap: 20px;
                }

                .hero-title-section {
                    display: flex;
                    align-items: center;
                    gap: 20px;
                }

                .hero-icon-wrapper {
                    width: 70px;
                    height: 70px;
                    border-radius: 20px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    box-shadow: 0 10px 15px -3px rgba(0, 0, 0, 0.1);
                }

                .hero-icon {
                    font-size: 2rem;
                    color: white;
                }

                .hero-title-section h1 {
                    margin: 0;
                    font-size: 2.2rem;
                    font-weight: 900;
                    color: #0F172A;
                    letter-spacing: -0.5px;
                }

                .hero-subtitle {
                    margin: 5px 0 0 0 !important;
                    font-size: 1.1rem !important;
                    font-weight: 600;
                    color: #64748B !important;
                }

                .status-badge {
                    padding: 10px 24px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    font-weight: 800;
                    font-size: 0.95rem;
                    box-shadow: 0 4px 10px rgba(0, 0, 0, 0.02);
                }

                .hero-stats-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(200px, 1fr));
                    gap: 20px;
                }

                .hero-stat {
                    background: #F8FAFC;
                    border-radius: 20px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    border: 1px solid #F1F5F9;
                    transition: all 0.3s ease;
                }

                .hero-stat:hover {
                    background: white;
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.04);
                    transform: translateY(-2px);
                }

                .hero-stat svg {
                    font-size: 2rem;
                    color: #6366F1;
                }

                .hero-stat .stat-label {
                    display: block;
                    font-size: 0.85rem;
                    font-weight: 700;
                    color: #64748B;
                    margin-bottom: 4px;
                }

                .hero-stat .stat-value {
                    display: block;
                    font-size: 1.3rem;
                    font-weight: 900;
                    color: #0F172A;
                }

                /* Renew Subscription Section */
                .renew-subscription-section {
                    margin-bottom: 30px;
                    display: flex;
                    justify-content: center;
                }

                .renew-subscription-btn {
                    background: linear-gradient(135deg, #6366F1 0%, #4F46E5 100%);
                    color: white;
                    border: none;
                    border-radius: 50px;
                    padding: 16px 40px;
                    font-size: 1.1rem;
                    font-weight: 800;
                    cursor: pointer;
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    box-shadow: 0 8px 20px rgba(99, 102, 241, 0.25);
                    transition: all 0.3s ease;
                    font-family: "Zain", sans-serif;
                }

                .renew-subscription-btn:hover {
                    box-shadow: 0 12px 25px rgba(99, 102, 241, 0.35);
                    transform: translateY(-2px);
                }

                .renew-icon {
                    font-size: 1.2rem;
                }

                .renew-subscription-btn:hover .renew-icon {
                    animation: rotate 1s linear infinite;
                }

                @keyframes rotate {
                    from {
                        transform: rotate(0deg);
                    }
                    to {
                        transform: rotate(360deg);
                    }
                }

                /* Details Grid */
                .subscription-details-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fit, minmax(300px, 1fr));
                    gap: 24px;
                    margin-bottom: 30px;
                }

                .detail-card {
                    background: white;
                    border-radius: 24px;
                    padding: 24px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
                    border: 1px solid rgba(255, 255, 255, 1);
                    transition: all 0.3s ease;
                }
                
                .detail-card:hover {
                    box-shadow: 0 10px 30px rgba(0, 0, 0, 0.06);
                    border-color: #F1F5F9;
                }

                .detail-card-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 20px;
                    padding-bottom: 15px;
                    border-bottom: 2px dashed #F1F5F9;
                }

                .detail-card-header svg {
                    font-size: 1.5rem;
                    color: #6366F1;
                }

                .detail-card-header h3 {
                    margin: 0;
                    font-size: 1.2rem;
                    color: #1E293B;
                    font-weight: 800;
                }

                .detail-card-content {
                    display: flex;
                    flex-direction: column;
                    gap: 12px;
                }

                .detail-row {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    padding: 14px;
                    background: #F8FAFC;
                    border-radius: 16px;
                }

                .detail-label {
                    font-size: 0.95rem;
                    color: #64748B;
                    font-weight: 700;
                }

                .detail-value {
                    font-size: 0.95rem;
                    color: #0F172A;
                    font-weight: 800;
                }

                .detail-number-highlight {
                    background: #DBEAFE;
                    color: #1D4ED8;
                    padding: 4px 10px;
                    border-radius: 10px;
                    font-weight: 900;
                    margin: 0 4px;
                    display: inline-block;
                }

                .notes-card .notes-text {
                    margin: 0;
                    padding: 16px;
                    background: #F8FAFC;
                    border-radius: 16px;
                    color: #475569;
                    line-height: 1.6;
                    font-weight: 600;
                    font-size: 0.95rem;
                }

                /* Delayed Days Card */
                .delayed-card {
                    border: 2px solid #6366f1;
                    background: linear-gradient(135deg, #FEF3C7 0%, #FDE68A 100%);
                }

                .delayed-card .detail-card-header svg {
                    color: #6366f1;
                }

                .delayed-card .detail-row {
                    background: rgba(255, 255, 255, 0.7);
                }

                .delayed-value {
                    color: #6366f1 !important;
                    font-size: 1.1rem !important;
                    font-weight: 800 !important;
                }

                /* Add-ons Section */
                .addons-section {
                    background: white;
                    border-radius: 32px;
                    padding: 30px;
                    margin-bottom: 30px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.02);
                    border: 1px solid rgba(255, 255, 255, 1);
                    position: relative;
                    overflow: hidden;
                }

                .addons-header {
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    margin-bottom: 25px;
                    padding-bottom: 20px;
                    border-bottom: 2px dashed #F1F5F9;
                }

                .addons-header svg {
                    font-size: 2rem;
                    color: #6366f1;
                }

                .addons-header h2 {
                    margin: 0;
                    font-size: 1.5rem;
                    color: #1E293B;
                    font-weight: 800;
                    flex: 1;
                }

                .addons-count {
                    background: #FEF3C7;
                    color: #D97706;
                    padding: 8px 18px;
                    border-radius: 12px;
                    font-size: 0.9rem;
                    font-weight: 800;
                }

                .addons-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(320px, 1fr));
                    gap: 25px;
                }

                .addon-card {
                    background: white;
                    border-radius: 24px;
                    padding: 25px;
                    display: flex;
                    align-items: center;
                    gap: 20px;
                    position: relative;
                    overflow: hidden;
                    border: 1px solid rgba(255, 255, 255, 1);
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
                    transition: all 0.3s ease;
                }

                .addon-card:hover {
                    border-color: #6366f1;
                    box-shadow: 0 10px 30px rgba(245, 158, 11, 0.1);
                    transform: translateY(-3px);
                }

                .addon-icon-bg {
                    width: 64px;
                    height: 64px;
                    background: linear-gradient(135deg, #FFFBEB 0%, #FEF3C7 100%);
                    border-radius: 16px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    color: #6366f1;
                    font-size: 1.8rem;
                    flex-shrink: 0;
                    border: 1px solid #FEF3C7;
                }

                .addon-content {
                    flex: 1;
                    z-index: 1;
                }

                .addon-header-info {
                    margin-bottom: 12px;
                }

                .addon-type-badge {
                    display: inline-block;
                    padding: 4px 10px;
                    background: #FEF3C7;
                    color: #D97706;
                    border-radius: 6px;
                    font-size: 0.75rem;
                    font-weight: 700;
                    margin-bottom: 6px;
                }

                .addon-content h4 {
                    margin: 0;
                    font-size: 1.2rem;
                    color: #1E293B;
                    font-weight: 800;
                }

                .addon-value-section {
                    display: flex;
                    justify-content: space-between;
                    align-items: center;
                    margin-top: 15px;
                    padding-top: 15px;
                    border-top: 1px dashed #E2E8F0;
                }

                .addon-label {
                    font-size: 0.9rem;
                    color: #64748B;
                    font-weight: 600;
                }

                .addon-value-badge {
                    display: flex;
                    align-items: center;
                    gap: 8px;
                    background: #F0FDF4;
                    color: #16A34A;
                    padding: 6px 14px;
                    border-radius: 50px;
                    font-weight: 800;
                    font-size: 1.1rem;
                    border: 1px solid #DCFCE7;
                }

                .addon-glow {
                    position: absolute;
                    top: -50%;
                    left: -50%;
                    width: 200%;
                    height: 200%;
                    background: radial-gradient(circle, rgba(245, 158, 11, 0.05) 0%, transparent 70%);
                    pointer-events: none;
                    z-index: 0;
                }

                /* Features Section */
                .features-section {
                    background: white;
                    border-radius: 32px;
                    padding: 30px;
                    box-shadow: 0 4px 20px rgba(0, 0, 0, 0.03);
                    border: 1px solid rgba(255, 255, 255, 1);
                }

                .features-header {
                    display: flex;
                    align-items: center;
                    gap: 12px;
                    margin-bottom: 25px;
                    padding-bottom: 20px;
                    border-bottom: 2px dashed #F1F5F9;
                }

                .features-header svg {
                    font-size: 1.8rem;
                    color: #6366F1;
                }

                .features-header h2 {
                    margin: 0;
                    font-size: 1.4rem;
                    color: #1E293B;
                    font-weight: 800;
                    flex: 1;
                }

                .features-count {
                    background: #EEF2FF;
                    color: #4F46E5;
                    padding: 6px 16px;
                    border-radius: 12px;
                    font-size: 0.85rem;
                    font-weight: 800;
                }

                .features-grid {
                    display: grid;
                    grid-template-columns: repeat(auto-fill, minmax(280px, 1fr));
                    gap: 20px;
                }

                .feature-card {
                    background: #F8FAFC;
                    border-radius: 20px;
                    padding: 20px;
                    display: flex;
                    align-items: center;
                    gap: 15px;
                    position: relative;
                    overflow: hidden;
                    transition: all 0.3s ease;
                    border: 2px solid transparent;
                }

                .feature-card.included {
                    background: white;
                    border-color: #ECFDF5;
                    box-shadow: 0 4px 15px rgba(16, 185, 129, 0.05);
                }

                .feature-card.excluded {
                    background: #F1F5F9;
                    opacity: 0.6;
                }

                .feature-card:hover {
                    box-shadow: 0 10px 20px rgba(0, 0, 0, 0.04);
                    transform: translateY(-2px);
                }

                .feature-icon-wrapper {
                    width: 50px;
                    height: 50px;
                    border-radius: 14px;
                    display: flex;
                    align-items: center;
                    justify-content: center;
                    flex-shrink: 0;
                }

                .feature-card.included .feature-icon-wrapper {
                    background: #ECFDF5;
                }

                .feature-card.excluded .feature-icon-wrapper {
                    background: #E2E8F0;
                }

                .feature-icon {
                    font-size: 1.5rem;
                }

                .feature-card.included .feature-icon {
                    color: #10B981;
                }

                .feature-card.excluded .feature-icon {
                    color: #94A3B8;
                }

                .feature-content {
                    flex: 1;
                }

                .feature-content h4 {
                    margin: 0 0 5px 0;
                    font-size: 1rem;
                    color: #1E293B;
                    font-weight: 700;
                }

                .feature-screen {
                    font-size: 0.85rem;
                    color: #64748B;
                    font-weight: 500;
                }

                .feature-badge {
                    position: absolute;
                    top: 10px;
                    left: 10px;
                    color: #6366f1;
                    font-size: 1rem;
                    animation: pulse 2s infinite;
                }

                @keyframes pulse {
                    0%, 100% {
                        opacity: 1;
                        transform: scale(1);
                    }
                    50% {
                        opacity: 0.7;
                        transform: scale(1.1);
                    }
                }

                /* Responsive Design */
                @media (max-width: 768px) {
                    .subscription-viewer-container {
                        padding: 16px;
                    }

                    .subscription-hero-card {
                        padding: 24px;
                    }

                    .hero-title-section {
                        flex-direction: column;
                        gap: 15px;
                    }

                    .hero-icon {
                        font-size: 2rem;
                    }

                    .hero-title-section h1 {
                        font-size: 1.5rem;
                    }

                    .hero-stats-grid {
                        grid-template-columns: 1fr;
                    }

                    .subscription-details-grid {
                        grid-template-columns: 1fr;
                    }

                    .addons-grid {
                        grid-template-columns: 1fr;
                    }

                    .features-grid {
                        grid-template-columns: 1fr;
                    }

                    .detail-row {
                        flex-direction: column;
                        align-items: flex-start;
                        gap: 8px;
                    }
                }
            `}</style>
        </motion.div>
    );
};

export default SubscriptionViewer;
